import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { HeartHand } from "@untitled-ui/icons-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import Container from "@/components/containers/Container";
import Tab from "@/components/containers/tabs/Tab";
import TabGroup from "@/components/containers/tabs/TabGroup";
import TabList from "@/components/containers/tabs/TabList";
import TabPanel from "@/components/containers/tabs/TabPanel";
import TabPanels from "@/components/containers/tabs/TabPanels";
import Button from "@/components/forms/Button";
import type { ChildRouteContext } from "@/root";

import TransactionTransferList from "@/modules/recognition/user/components/TransactionTransferList";
import TransactionModal from "@/modules/recognition/user/components/transaction/TransactionModal";
import TransactionSentList from "@/modules/recognition/user/components/transaction/TransactionSentList";
import {
  fetchPeriodQueryOptions,
  fetchTransactionTransfersMeReceivedQueryOptions,
} from "@/modules/recognition/user/services";

export const Route = createFileRoute(
  "/_panel/_recognition/recognition/transactions/transfers",
)({
  beforeLoad: ({ context }) =>
    ({
      crumb: [
        {
          label: context.t("View all", { ns: "core.shared" }),
        },
      ],
    }) satisfies ChildRouteContext,
  loader: (opts) =>
    Promise.all([
      opts.context.queryClient.prefetchQuery(
        fetchTransactionTransfersMeReceivedQueryOptions(),
      ),
      opts.context.queryClient.prefetchQuery(
        fetchPeriodQueryOptions("current"),
      ),
    ]),
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation("recognition.user");
  const [open, setOpen] = useState(false);

  const { data: currentPeriod } = useSuspenseQuery(
    fetchPeriodQueryOptions("current"),
  );
  const hasActivePeriod = !!currentPeriod;

  return (
    <Container
      title={t("Treasure")}
      description={t("View your received and donated appreciations")}
      className="flex h-full flex-col"
      action={
        hasActivePeriod && (
          <>
            <Button
              className="w-full sm:w-80"
              endIcon={<HeartHand className="h-4 w-4" />}
              onClick={() => setOpen(true)}
            >
              {t("Appreciate your colleague")}
            </Button>
            {open && (
              <TransactionModal open={open} onClose={() => setOpen(false)} />
            )}
          </>
        )
      }
    >
      <TabGroup>
        <TabList>
          <Tab>{t("Received", { ns: "recognition.shared" })}</Tab>
          <Tab>{t("Sent", { ns: "recognition.shared" })}</Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            <TransactionTransferList />
          </TabPanel>
          <TabPanel>
            <TransactionSentList />
          </TabPanel>
        </TabPanels>
      </TabGroup>
    </Container>
  );
}
